import {
  Component,
  effect,
  OnInit,
  OnDestroy,
  AfterViewInit,
  Signal,
  ViewChild,
  HostListener
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Location} from '@angular/common';
import {ProductService} from '../../services/product/product.service';
import {faArrowsRotate, faBarcode, faCartShopping, faChevronDown, faImage, faMagnifyingGlass, faPen, faPlus, faStar, faTrash, faUtensils} from '@fortawesome/free-solid-svg-icons';
import {Household, HouseholdProviderSetting} from '../../models/household';
import {HouseholdService} from '../../services/property/household.service';
import {Context} from '../../models/context';
import {Product, ProductAttribute, ProductCode, ProductPriceDataPoint, ProductPriceResponse, ProductPriceSeries, ProductTask, ProviderStatus, SyncStatusResponse} from '../../models/product';
import type {EChartsCoreOption} from 'echarts/core';
import {Store} from '../../models/store';
import {forkJoin, interval, Observable, Subject, timer} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {InsightsService} from '../../services/insights/insights.service';
import {InsightsGroupTotal, InsightsMoneyTotal, InsightsUnitTotal} from '../../models/insights';
import {StoreService} from '../../services/property/store.service';
import {StockService} from "../../services/stock/stock.service";
import {UiToastService} from '../../shared/ui';
import {ContextService} from "../../services/context/context-signal.service";
import {environment} from '../../env';
import {BarcodeService} from "../../services/barcode/barcode.service";
import {ConfirmService} from "../../services/confirm/confirm.service";
import {StockItem} from '../../models/stock';
import {ProductImageDialogComponent} from "../../modals/product-image-dialog/product-image-dialog.component";
import {PageHeaderService} from "../../services/page-header/page-header.service";
import {PageAction} from "../../components/app-shell/page-action.model";
import {ProductNamePipe} from "../../pipes/product-name.pipe";
import {SnakeToLabelPipe} from "../../pipes/snake-to-label.pipe";
import {AppLogger, AppLoggerService} from '../../core/logging';
import {ShoppingService} from "../../services/shopping/shopping.service";
import {ShoppingList} from "../../models/shopping-list";
import {AddItemProductRequest, ProductListPresence} from "../../models/shopping-list-item.model";
import {AppEventsService} from "../../services/app-events/app-events.service";
import {provideViewContext} from "../../services/view-context/provide-view-context";

export type InsightsRange = '30d' | '90d' | '12m' | 'all';

/** An amount split into the number and the unit it should be shown in. */
export interface DisplayAmount {
  value: number;
  unit: string;
}

/** One monthly bucket of the trend chart, in the chart's display unit. */
interface InsightsTrendBucket {
  label: string;
  year: number;
  from: Date;
  to: Date;
  /** The current month is still filling up, so it isn't comparable to the rest. */
  partial: boolean;
}

@Component({
  selector: 'app-product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.scss'],
  standalone: false
})
export class ProductDetailsComponent implements OnInit, OnDestroy, AfterViewInit {

  private readonly tag = '[ProductDetailsComponent]';
  private readonly log: AppLogger;

  context: Signal<Context> = this.contextService.context;

  faEdit = faPen;
  faAdd = faPlus;
  faTrash = faTrash;
  faSync = faArrowsRotate;
  faCartShopping = faCartShopping;
  faStar = faStar;
  faChevronDown = faChevronDown;
  faUtensils = faUtensils;

  compareMode = true;
  selectedProviders: string[] = [];
  pickerOpenIndex: number | null = null;
  providerSettings: HouseholdProviderSetting[] = [];
  maxCompareProviders = window.innerWidth >= 640 ? 3 : 2;

  household: Household;
  store: Store;
  stores: Store[];

  code: string;
  productId: number;
  sub: any;
  product: Product;
  productSyncStatus: SyncStatusResponse;
  activeTasks: ProductTask[] = [];
  private hadActiveTasks = false;

  private static readonly TASKS_POLL_ACTIVE_MS = 1000;
  private static readonly TASKS_POLL_IDLE_MS = 10000;

  private barcodeUrlCache = new Map<string, Observable<string>>();
  loadedCodes = new Set<string>();

  private destroy$ = new Subject<void>();

  // Trigger Updates to Sub Components on Change
  addProductSubject: Subject<boolean> = new Subject<boolean>();

  isSyncing = false;

  isBarcodesCollapsed = true;

  draggedItem: StockItem | null = null;
  draggedFromStoreId: number | null = null;
  isDragActive = false;

  // Tab state
  activeTab: string = 'stock';

  // Variable quantity add-to-store state
  activeAddStore: number | null = null;
  variableItemQuantity: string = '';

  productPrice: ProductPriceResponse | null = null;
  priceLoading = false;
  priceChartOptions: EChartsCoreOption = {};
  hasPriceData = false;

  // Insights tab — fetched lazily on first open, then per range change.
  insightsRange: InsightsRange = '12m';
  insights: InsightsGroupTotal | null = null;
  insightsLoading = false;
  insightsLoaded = false;
  insightsFailed = false;
  insightsRangeLabel = '';
  insightsTrendOptions: EChartsCoreOption = {};
  hasInsightsTrend = false;
  insightsTrendLabel = '';

  productImageLoadError = false;
  activeShoppingList: ShoppingList | null = null;
  productListPresence: ProductListPresence | null = null;
  showCodesModal = false;

  private productNamePipe = new ProductNamePipe();
  private snakeToLabelPipe = new SnakeToLabelPipe();

  @ViewChild(ProductImageDialogComponent) productImageDialog!: ProductImageDialogComponent;

  constructor(
    private contextService: ContextService,
    private confirm: ConfirmService,
    public router: Router,
    private location: Location,
    private storeService: StoreService,
    private stockService: StockService,
    private toastService: UiToastService,
    private route: ActivatedRoute,
    private productService: ProductService,
    private barcodeService: BarcodeService,
    private appLogger: AppLoggerService,
    private pageHeaderService: PageHeaderService,
    private shoppingService: ShoppingService,
    private householdService: HouseholdService,
    private insightsService: InsightsService,
    private appEvents: AppEventsService
  ) {
    this.log = this.appLogger.createForClass(ProductDetailsComponent);

    provideViewContext('product_detail', () =>
      this.product
        ? [{type: 'product', id: this.product.id, label: this.productNamePipe.transform(this.product)}]
        : []
    );

    this.appEvents.stockChanged$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.addProductSubject.next(true));

    effect(() => {
      const c = this.context();
      if (!c?.isSet?.()) return;
      this.household = c.household;
      this.loadProduct(this.productId);
      this.loadStores(c.household.id);
      this.loadActiveShoppingList(c.household.id);
      this.loadProviderSettings(c.household.id);
      // Insights are household-scoped, so a household switch invalidates them.
      this.resetInsights();
      this.ensureInsightsLoaded();
    });

  }

  ngOnInit(): void {
    this.sub = this.route.params.subscribe(params => {
      this.productId = params.id;
      this.log.debug('Product route active', {productId: this.productId});
      this.loadProductPrice(this.productId);
      this.pollActiveTasks(this.productId);
      this.resetInsights();
      this.ensureInsightsLoaded();
    });
    interval(10000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.productId) this.loadProductSyncStatus(this.productId);
      });
    const tabParam = this.route.snapshot.queryParamMap.get('tab');
    if (tabParam) {
      this.activeTab = tabParam;
    }
  }

  private pollActiveTasks(productId: number): void {
    this.productService.getActiveProductTasks(productId).pipe(takeUntil(this.destroy$)).subscribe({
      next: tasks => {
        this.activeTasks = tasks;
        this.updateTasksBadge();

        const isActive = tasks.length > 0;
        if (this.hadActiveTasks && !isActive) {
          this.loadProduct(productId);
        }
        this.hadActiveTasks = isActive;

        this.scheduleNextTasksPoll(productId, isActive
          ? ProductDetailsComponent.TASKS_POLL_ACTIVE_MS
          : ProductDetailsComponent.TASKS_POLL_IDLE_MS);
      },
      error: err => {
        this.log.error('Failed to load active product tasks', err, {productId});
        this.scheduleNextTasksPoll(productId, ProductDetailsComponent.TASKS_POLL_IDLE_MS);
      }
    });
  }

  private updateTasksBadge(): void {
    if (this.activeTasks.length === 0) {
      this.pageHeaderService.clearTitleBadge();
      return;
    }
    const tooltip = this.activeTasks
      .map(t => `${this.snakeToLabelPipe.transform(t.type)} — ${this.snakeToLabelPipe.transform(t.status)}`)
      .join('\n');
    this.pageHeaderService.setTitleBadge({
      label: `Syncing (${this.activeTasks.length})`,
      tooltip
    });
  }

  private scheduleNextTasksPoll(productId: number, delayMs: number): void {
    timer(delayMs).pipe(takeUntil(this.destroy$)).subscribe(() => this.pollActiveTasks(productId));
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      // Register declarative toolbar actions for the new AppShell.
      this.refreshPageActions();

      // Set back button to use browser history (preserves query params)
      this.pageHeaderService.setBackButton({
        label: 'Back',
        preferHistoryBack: true,
        display: 'auto',
        fallbackUrl: '/products'
      });
    }, 0);
  }

  /**
   * (Re)publish toolbar actions to the AppShell. Called on init and whenever
   * the product reloads, so the buttons' disabled state stays in sync.
   */
  private refreshPageActions(): void {
    const actions: PageAction[] = [
      {
        type: 'button',
        id: 'edit',
        label: 'Edit',
        icon: this.faEdit,
        variant: 'secondary',
        pinned: false, // always visible: edit is the primary task on this page
        disabled: !this.product,
        onClick: () => this.openEditProduct(this.product?.id),
      },
      {
        type: 'button',
        id: 'delete',
        label: 'Delete',
        icon: this.faTrash,
        variant: 'danger',
        disabled: !this.product,
        alwaysInSubMenu: true,
        onClick: () =>
          this.onDelete({ id: this.product?.id, name: this.product?.name }),
      },
    ];
    this.pageHeaderService.setPageActions(actions);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.pageHeaderService.clearAll();
  }

  loadProduct(productId: number): void {
    this.productService.getProduct(productId).subscribe({
      next: product => {
        this.product = product;
        this.productImageLoadError = false;
        this.loadProductSyncStatus(productId);

        // Set page title to product name
        const productName = this.productNamePipe.transform(product);
        this.pageHeaderService.setPageTitle(productName);

        // Re-publish actions so disabled flags reflect the loaded product.
        this.refreshPageActions();
        this.initCompareProviders();
        this.loadProductPresence();
      }, error: err => {
        if (err.status === 404) {
          this.router.navigate(['/404']);
        }
      }
    });
  }

  loadProviderSettings(householdId: number): void {
    this.householdService.getProviderSettings(householdId).subscribe({
      next: settings => {
        this.providerSettings = settings.filter(s => s.is_enabled);
        this.initCompareProviders();
      }
    });
  }

  private providerPriority(key: string): number {
    const setting = this.providerSettings.find(s => s.provider_key === key);
    return setting?.priority ?? Number.MAX_SAFE_INTEGER;
  }

  loadProductSyncStatus(productId: number): void {
    this.productService.getProductSyncStatus(productId).subscribe({
      next: productSyncStatus => {
        this.log.debug('Product sync status loaded', {productId, productSyncStatus});
        this.productSyncStatus = productSyncStatus;
      },
      error: () => {
        // Error is expected if sync status is not available
      }
    });
  }

  triggerSync(ev?: MouseEvent): void {
    ev?.preventDefault();
    ev?.stopPropagation();
    if (this.isSyncing) return;
    this.isSyncing = true;
    this.productService.triggerProductSync(this.productId).subscribe({
      next: () => {
        this.isSyncing = false;
        this.toastService.success('Sync triggered');
        this.loadProductSyncStatus(this.productId);
      },
      error: () => {
        this.isSyncing = false;
      }
    });
  }

  openEditProduct(productId: number): void {
    this.router.navigate(['/products', productId, 'edit']);
  }

  /** Whether this product is a self-cooked meal (created from a recipe). */
  isMeal(): boolean {
    return this.product?.origin === 'self_cooked_meal';
  }

  /** Navigate to the recipe a meal product was cooked from. */
  openRecipe(recipeId: number): void {
    this.router.navigate(['/recipe', recipeId]);
  }

  /**
   * Triggers reloading of stock items in child components.
   * Note: The child stock list only reloads when its [storeId] matches the currently selected context store.
   */
  onStockChange(): void {
    this.log.debug('Stock items changed');
    this.addProductSubject.next(true);
  }

  /**
   * Ensure both store lists refresh after a move (drag&drop).
   *
   * We intentionally do NOT switch the global context store here.
   * Instead we just fire the existing update trigger which tells each
   * <app-stock-items> instance to reload its own data.
   */
  private reloadStores(storeIds: Array<number | null | undefined>): void {
    const ids = (storeIds || []).filter((id): id is number => typeof id === 'number' && id > 0);
    const uniqueIds = Array.from(new Set(ids));
    if (uniqueIds.length === 0) {
      return;
    }

    // Currently the updateTrigger is broadcast to all rendered store lists.
    // We emit twice to handle cases where a component is still finishing a previous refresh.
    this.addProductSubject.next(true);
    setTimeout(() => this.addProductSubject.next(true), 0);
  }

  addProductToStock(productId: number, storeId: number, itemQuantity?: string): void {
    this.stockService.addProductToStock(productId, storeId, undefined, itemQuantity || undefined).subscribe(data => {
      this.toastService.success(`Added to store`);
      this.log.info('Product added to stock', {productId, storeId, response: data});
      this.addProductSubject.next(true);
      this.activeAddStore = null;
      this.variableItemQuantity = '';
    });
  }

  onAddClick(productId: number, storeId: number): void {
    if (this.product?.quantity_type === 'variable') {
      this.activeAddStore = this.activeAddStore === storeId ? null : storeId;
      this.variableItemQuantity = this.product.quantity?.value ?? '';
    } else {
      this.addProductToStock(productId, storeId);
    }
  }

  confirmVariableAdd(productId: number, storeId: number): void {
    this.addProductToStock(productId, storeId, this.variableItemQuantity || undefined);
  }

  loadStores(householdId: number): void {
    this.storeService.stores(householdId).subscribe(data => {
      this.stores = data;
    });
  }

  loadActiveShoppingList(householdId: number): void {
    this.shoppingService.getLists(householdId).subscribe({
      next: lists => {
        this.activeShoppingList = lists.find(l => l.is_active) ?? lists[0] ?? null;
      }
    });
  }

  /**
   * Adds the product to the active list. When `quantity` is a positive number
   * (from the restock nudge) it is carried as the item's subtitle so the list
   * reflects how many are needed to reach the desired-stock target.
   */
  addToShoppingList(quantity?: number): void {
    if (!this.activeShoppingList || !this.product?.id) return;
    const listName = this.activeShoppingList.name;
    const toastId = this.toastService.loading(`Adding to "${listName}"…`);
    const request: AddItemProductRequest = {
      client_id: crypto.randomUUID(),
      product_id: this.product.id
    };
    if (quantity && quantity > 0) {
      request.subtitle = String(quantity);
    }
    this.shoppingService.addItem(this.household.id, this.activeShoppingList.id, request).subscribe({
      next: () => {
        this.toastService.update(toastId, `Added to "${listName}"`, { variant: 'success' });
        this.loadProductPresence();
      },
      error: () => {
        this.toastService.update(toastId, `Failed to add to "${listName}"`, { variant: 'error' });
      }
    });
  }

  /** Loads whether this product is already a pending item on the active list. */
  loadProductPresence(): void {
    if (!this.household?.id || !this.product?.id) return;
    this.shoppingService.getActiveListProduct(this.household.id, this.product.id).subscribe({
      next: presence => {
        this.productListPresence = presence;
      },
      error: () => {
        this.productListPresence = null;
      }
    });
  }

  getProviderLogoPath(provider: string): Observable<String> {
    if (!provider) return new Observable(observer => {
      observer.next('assets/img/provider-logos/unknown.png');
      observer.complete();
    })

    return new Observable(observer => {
      observer.next('assets/img/provider-logos/' +
        provider.toLowerCase().replace(/ /g, '-') +
        '.png');
      observer.complete();
    })
  }

  getProviderName(provider: string): string {
    if (!provider) return "";

    switch (provider.toLowerCase()) {
      case "migros":
        return "Migros";
      case "off":
        return "Open Food Facts";
      default:
        return provider;
    }
  }

  public getBarcodeUrl(code: string, width: number, height: number): Observable<String> {

    if (!this.barcodeUrlCache.has(code)) {
      this.barcodeUrlCache.set(code, this.barcodeService.getBarcodeImageUrl(code, width, height));
    }

    return this.barcodeUrlCache.get(code)!;
  }

  protected readonly environment = environment;


  onImageLoad(code: string): void {
    this.loadedCodes.add(code);
  }

  onImageError(code: string): void {
    // Optionally handle image load failure
    this.loadedCodes.delete(code);
  }


  async onDelete(product: { id: number; name: string }) {
    const ok = await this.confirm.ask({
      title: 'Delete product',
      message: `Do you really want to delete "${product.name}"? This will remove all associated stocks as well!`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmBtnClass: 'btn-danger',
      modalOptions: {
        size: 'md'
      }
    });

    if (!ok) return;

    this.productService.deleteProduct(product.id).subscribe({
      next: () => {
        this.toastService.success(`Product "${product.name}" deleted.`);
        this.router.navigate(['/products']);
      },
      error: (err) => {
        this.toastService.error(`Failed to delete product "${product.name}".`);
        this.log.error('Failed to delete product', err);
      }
    });
  }

  normalizeUrl(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    let url = value.trim();
    if (!url) {
      return '';
    }
    if (!/^https?:\/\//i.test(url)) {
      url = 'https://' + url;
    }
    return url;
  }

  protected readonly Object = Object;

  onStockItemDragStart(payload: { item: StockItem; fromStoreId: number }): void {
    this.draggedItem = payload.item;
    this.draggedFromStoreId = payload.fromStoreId;
    this.isDragActive = true;
  }

  onStockItemDragEnd(): void {
    this.draggedItem = null;
    this.draggedFromStoreId = null;
    this.isDragActive = false;
  }

  moveStockItemToStore(stockItem: StockItem, fromStoreId: number, toStoreId: number): void {
    const itemId = stockItem.id;

    this.stockService.moveStockItem(itemId, toStoreId).subscribe({
      next: () => {
        this.toastService.success(`Moved item #${itemId} from store ${fromStoreId} to store ${toStoreId}`);
        this.reloadStores([fromStoreId, toStoreId]);
      },
      error: (err) => {
        this.toastService.error('Failed to move stock item');
        this.log.error('Failed to move stock item', err);
      }
    });
  }

  onDropOnStore(targetStoreId: number): void {
    if (!this.draggedItem || this.draggedFromStoreId == null || targetStoreId == null) {
      this.onStockItemDragEnd();
      return;
    }
    if (targetStoreId === this.draggedFromStoreId) {
      this.onStockItemDragEnd();
      return;
    }

    this.moveStockItemToStore(this.draggedItem, this.draggedFromStoreId, targetStoreId);

    this.onStockItemDragEnd();
  }

  onImageEditClick(): void {
    this.productImageDialog.open();
  }

  /** Open image management straight on the search panel. */
  onImageSearchClick(): void {
    this.productImageDialog.open(true);
  }

  onImageDialogClosed(): void {
    this.log.debug('Image dialog closed');
    // Reload product data when modal is closed
    // Use direct subscribe to avoid triggering effect recursion
    this.productService.getProduct(this.productId).subscribe({
      next: product => {
        this.log.debug('Product reloaded', {product});
        this.product = product;
        this.loadProductSyncStatus(this.productId);
      },
      error: err => {
        this.log.error('Failed to reload product', err, {productId: this.productId});
        if (err.status === 404) {
          this.router.navigate(['/404']);
        }
      }
    });
  }

  protected readonly faPlus = faPlus;

  get productTabs() {
    const tabs = [
      { id: 'stock', label: 'Stock' },
      { id: 'attributes', label: 'Details' },
    ];
    if (this.hasPriceData) {
      tabs.push({ id: 'price', label: 'Price' });
    }
    // Always offered: an empty report is itself worth saying out loud, and the
    // only way to find out is to ask.
    tabs.push({ id: 'insights', label: 'Insights' });
    return tabs;
  }

  onTabChange(tabId: string): void {
    this.activeTab = tabId;
    this.replaceTabParam(tabId);
    this.ensureInsightsLoaded();
  }

  private replaceTabParam(tabId: string): void {
    const urlTree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: { tab: tabId },
      queryParamsHandling: 'merge'
    });
    this.location.replaceState(this.router.serializeUrl(urlTree));
  }

  loadProductPrice(productId: number): void {
    this.priceLoading = true;
    this.productService.getProductPrice(productId).subscribe({
      next: price => {
        this.productPrice = price;
        this.hasPriceData = price.series.length > 0;
        this.priceLoading = false;
        if (this.hasPriceData) {
          this.buildPriceChartOptions(price);
        } else if (this.activeTab === 'price') {
          this.activeTab = 'stock';
          this.replaceTabParam('stock');
        }
      },
      error: () => {
        this.priceLoading = false;
      }
    });
  }

  private buildPriceChartOptions(price: ProductPriceResponse): void {
    const currency = price.series[0]?.currency ?? '';
    this.priceChartOptions = {
      color: price.series.map((_, i) => this.priceColor(i)),
      tooltip: {
        trigger: 'axis',
        valueFormatter: (v: number) => `${v.toFixed(2)} ${currency}`
      },
      legend: { bottom: 0 },
      grid: { left: 60, right: 20, top: 20, bottom: 40 },
      xAxis: { type: 'time' },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: (v: number) => `${v.toFixed(2)} ${currency}` }
      },
      series: price.series.map((s: ProductPriceSeries) => ({
        name: this.getProviderName(s.provider),
        type: 'line',
        smooth: true,
        data: s.data.map((d: ProductPriceDataPoint) => [d.timestamp, d.value])
      }))
    };
  }

  /** Fixed-order categorical palette (colorblind-safe), shared between the price KPI cards and the chart lines. */
  private readonly priceSeriesPalette = [
    '#2a78d6', '#008300', '#e87ba4', '#eda100', '#1baf7a', '#eb6834', '#4a3aa7', '#e34948'
  ];

  priceColor(index: number): string {
    return this.priceSeriesPalette[index % this.priceSeriesPalette.length];
  }

  /** catalog_id of the cheapest provider, or null when there's nothing to compare against. */
  get cheapestCatalogId(): string | null {
    const series = this.productPrice?.series ?? [];
    if (series.length < 2) return null;
    return series.reduce((min, s) => s.current_price < min.current_price ? s : min).catalog_id;
  }

  /** Direction/size of the current price vs. the most recent recorded history point. */
  priceTrend(s: ProductPriceSeries): { direction: 'up' | 'down'; deltaPct: number } | null {
    const previous = s.data?.length ? s.data[s.data.length - 1].value : null;
    if (!previous || previous === s.current_price) return null;
    const deltaPct = Math.abs((s.current_price - previous) / previous) * 100;
    return { direction: s.current_price > previous ? 'up' : 'down', deltaPct };
  }

  // ── Insights ───────────────────────────────────────────────────────────────
  //
  // There is no per-product stats endpoint. The household stock-insights report
  // takes a product_id filter, which narrows it to exactly one group — that's
  // the whole data source for this tab.

  private static readonly INSIGHTS_RANGES: { id: InsightsRange; label: string }[] = [
    { id: '30d', label: '30 days' },
    { id: '90d', label: '90 days' },
    { id: '12m', label: '12 months' },
    { id: 'all', label: 'All time' }
  ];

  /** Semantic pair: outflow that was used, vs outflow that was binned. */
  private static readonly INSIGHTS_CONSUMED_COLOR = '#2a78d6';
  private static readonly INSIGHTS_WASTED_COLOR = '#e34948';

  /** Recessive ink for chart axes and legend, legible on both themes. */
  private static readonly CHART_INK = '#8a94a0';

  private insightsFrom: Date | null = null;
  private insightsTo: Date = new Date();

  get insightsRanges() {
    return ProductDetailsComponent.INSIGHTS_RANGES;
  }

  get consumedColor() {
    return ProductDetailsComponent.INSIGHTS_CONSUMED_COLOR;
  }

  get wastedColor() {
    return ProductDetailsComponent.INSIGHTS_WASTED_COLOR;
  }

  private resetInsights(): void {
    this.insights = null;
    this.insightsLoaded = false;
    this.insightsFailed = false;
    this.hasInsightsTrend = false;
    this.insightsTrendOptions = {};
  }

  onInsightsRangeChange(range: InsightsRange): void {
    if (range === this.insightsRange) return;
    this.insightsRange = range;
    this.resetInsights();
    this.ensureInsightsLoaded();
  }

  retryInsights(): void {
    this.resetInsights();
    this.ensureInsightsLoaded();
  }

  /**
   * Loads on first open of the tab, and again after anything that invalidates
   * the report. Cheap to call repeatedly — it's guarded on every precondition.
   */
  private ensureInsightsLoaded(): void {
    if (this.activeTab !== 'insights') return;
    if (this.insightsLoaded || this.insightsLoading) return;
    if (!this.household?.id || !this.productId) return;
    this.loadInsights();
  }

  private loadInsights(): void {
    const {from, to} = this.insightsWindow();
    const householdId = this.household.id;
    const productId = this.productId;

    this.insightsFrom = from;
    this.insightsTo = to;
    this.insightsRangeLabel = this.formatWindowLabel(from, to);
    this.insightsLoading = true;
    this.insightsFailed = false;

    this.insightsService.getProductInsights(householdId, productId, from?.toISOString(), to.toISOString())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: group => {
          this.insights = group;
          this.insightsLoading = false;
          this.insightsLoaded = true;
          this.loadInsightsTrend(householdId, productId);
        },
        error: err => {
          this.log.error('Failed to load product insights', err, {productId, householdId});
          this.insightsLoading = false;
          this.insightsLoaded = true;
          this.insightsFailed = true;
        }
      });
  }

  /** The window the totals cover. `from` is null for all-time — no lower bound. */
  private insightsWindow(): { from: Date | null; to: Date } {
    const to = new Date();
    switch (this.insightsRange) {
      case 'all':
        return {from: null, to};
      case '12m':
        // Whole months, so the totals line up with the trend buckets.
        return {from: new Date(to.getFullYear(), to.getMonth() - 11, 1), to};
      default: {
        const days = this.insightsRange === '90d' ? 90 : 30;
        const from = new Date(to);
        from.setDate(from.getDate() - days);
        from.setHours(0, 0, 0, 0);
        return {from, to};
      }
    }
  }

  /** Days the loaded window spans, or null for all-time where there's no start. */
  private get insightsWindowDays(): number | null {
    if (!this.insightsFrom) return null;
    const ms = this.insightsTo.getTime() - this.insightsFrom.getTime();
    return Math.max(1, Math.round(ms / 86400000));
  }

  /**
   * Monthly buckets for the trend chart. Each one costs a request — the endpoint
   * has no interval parameter — so this caps at 12, and is skipped for 30 days
   * where a single partial month has nothing to be compared against.
   */
  private trendBuckets(): InsightsTrendBucket[] {
    if (this.insightsRange === '30d') return [];
    const count = this.insightsRange === '90d' ? 3 : 12;
    const now = new Date();
    const buckets: InsightsTrendBucket[] = [];

    for (let i = count - 1; i >= 0; i--) {
      const from = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
      buckets.push({
        label: from.toLocaleDateString(undefined, {month: 'short'}),
        year: from.getFullYear(),
        from,
        to: i === 0 ? now : new Date(nextMonth.getTime() - 1),
        partial: i === 0
      });
    }
    return buckets;
  }

  private loadInsightsTrend(householdId: number, productId: number): void {
    // One request per bucket, so don't spend twelve of them establishing that a
    // product with no recorded quantities also has none month by month.
    if (!this.primaryUnit) {
      this.hasInsightsTrend = false;
      return;
    }

    const buckets = this.trendBuckets();
    if (!buckets.length) {
      this.hasInsightsTrend = false;
      return;
    }

    forkJoin(buckets.map(b => this.insightsService.getProductInsights(
      householdId, productId, b.from.toISOString(), b.to.toISOString()
    ))).pipe(takeUntil(this.destroy$)).subscribe({
      next: groups => this.buildInsightsTrendOptions(buckets, groups),
      error: err => {
        this.log.error('Failed to load insights trend', err, {productId, householdId});
        this.hasInsightsTrend = false;
      }
    });
  }

  private buildInsightsTrendOptions(buckets: InsightsTrendBucket[], groups: (InsightsGroupTotal | null)[]): void {
    const primary = this.primaryUnit;
    if (!primary) {
      this.hasInsightsTrend = false;
      return;
    }

    const rows = buckets.map((_, i) => {
      const totals = groups[i]?.quantities?.find(q => q.unit === primary.unit);
      return {
        consumed: this.numeric(totals?.consumed),
        wasted: this.numeric(totals?.wasted)
      };
    });

    const peak = Math.max(...rows.map(r => r.consumed + r.wasted));
    if (peak <= 0) {
      this.hasInsightsTrend = false;
      return;
    }

    // One scale for the whole chart, taken off the peak, so bars stay comparable.
    const scale = this.amountScale(peak, primary.unit);
    const fmt = (v: number) => `${v.toFixed(v >= 100 ? 0 : 1)} ${scale.label}`;
    const ink = ProductDetailsComponent.CHART_INK;
    const roundedTop = [4, 4, 0, 0];

    const first = buckets[0];
    const last = buckets[buckets.length - 1];
    this.insightsTrendLabel = `${first.label} ${first.year} – ${last.label} ${last.year}`;
    this.hasInsightsTrend = true;

    this.insightsTrendOptions = {
      color: [ProductDetailsComponent.INSIGHTS_CONSUMED_COLOR, ProductDetailsComponent.INSIGHTS_WASTED_COLOR],
      tooltip: {
        trigger: 'axis',
        axisPointer: {type: 'shadow'},
        valueFormatter: (v: number) => fmt(v)
      },
      legend: {bottom: 0, textStyle: {color: ink}, itemHeight: 8, itemWidth: 12},
      grid: {left: 58, right: 16, top: 16, bottom: 56},
      xAxis: {
        type: 'category',
        // The year is printed only where it turns over.
        data: buckets.map((b, i) => (i === 0 || b.year !== buckets[i - 1].year) ? `${b.label}\n${b.year}` : b.label),
        axisLabel: {color: ink, fontSize: 11, lineHeight: 15},
        axisLine: {lineStyle: {color: 'rgba(138,148,160,0.35)'}},
        axisTick: {show: false}
      },
      yAxis: {
        type: 'value',
        axisLabel: {color: ink, fontSize: 11, formatter: (v: number) => fmt(v)},
        splitLine: {lineStyle: {color: 'rgba(138,148,160,0.18)'}}
      },
      series: [
        {
          name: 'Used',
          type: 'bar',
          stack: 'flow',
          barMaxWidth: 28,
          data: rows.map((r, i) => ({
            value: r.consumed / scale.divisor,
            // Round only the actual top of the stack, and fade the month that
            // is still filling up so it doesn't read as a real drop.
            itemStyle: {
              borderRadius: r.wasted > 0 ? 0 : roundedTop,
              opacity: buckets[i].partial ? 0.5 : 1
            }
          }))
        },
        {
          name: 'Thrown away',
          type: 'bar',
          stack: 'flow',
          barMaxWidth: 28,
          data: rows.map((r, i) => ({
            value: r.wasted / scale.divisor,
            itemStyle: {borderRadius: roundedTop, opacity: buckets[i].partial ? 0.5 : 1}
          }))
        }
      ]
    };
  }

  /** Parses one of the report's string decimals. */
  numeric(value: string | undefined | null): number {
    if (value == null) return 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  /** Divisor and label for showing a base-unit amount at a sensible magnitude. */
  private amountScale(reference: number, unit: string): { divisor: number; label: string } {
    if (unit === 'g' && reference >= 1000) return {divisor: 1000, label: 'kg'};
    if (unit === 'ml' && reference >= 1000) return {divisor: 1000, label: 'L'};
    if (unit === 'piece') return {divisor: 1, label: 'pcs'};
    return {divisor: 1, label: unit};
  }

  /** Scales a base-unit amount (g / ml / piece) for display. */
  displayAmount(baseAmount: number, unit: string): DisplayAmount {
    const scale = this.amountScale(baseAmount, unit);
    return {value: baseAmount / scale.divisor, unit: scale.label};
  }

  /** The unit that dominates this product's flow; drives the tiles and the chart. */
  get primaryUnit(): InsightsUnitTotal | null {
    const quantities = this.insights?.quantities ?? [];
    if (!quantities.length) return null;
    const flow = (q: InsightsUnitTotal) => this.numeric(q.consumed) + this.numeric(q.added);
    return quantities.reduce((best, q) => flow(q) > flow(best) ? q : best);
  }

  /** Any further units, listed rather than folded into a total that can't exist. */
  get secondaryUnits(): InsightsUnitTotal[] {
    const primary = this.primaryUnit;
    return (this.insights?.quantities ?? []).filter(q => q !== primary);
  }

  get primaryMoney(): InsightsMoneyTotal | null {
    const money = this.insights?.money ?? [];
    if (!money.length) return null;
    return money.reduce((best, m) => this.numeric(m.spent) > this.numeric(best.spent) ? m : best);
  }

  /** Further currencies — there is no exchange rate anywhere in the system. */
  get secondaryMoney(): InsightsMoneyTotal[] {
    const primary = this.primaryMoney;
    return (this.insights?.money ?? []).filter(m => m !== primary);
  }

  get hasInsightsData(): boolean {
    return (this.insights?.quantities?.length ?? 0) > 0 || this.insightsPurchases > 0;
  }

  get consumedAmount(): DisplayAmount | null {
    const unit = this.primaryUnit;
    return unit ? this.displayAmount(this.numeric(unit.consumed), unit.unit) : null;
  }

  get addedAmount(): DisplayAmount | null {
    const unit = this.primaryUnit;
    return unit ? this.displayAmount(this.numeric(unit.added), unit.unit) : null;
  }

  get wastedAmount(): DisplayAmount | null {
    const unit = this.primaryUnit;
    return unit ? this.displayAmount(this.numeric(unit.wasted), unit.unit) : null;
  }

  /** Share of everything that left stock that was thrown away rather than used. */
  get wasteRate(): number | null {
    const unit = this.primaryUnit;
    if (!unit) return null;
    const outflow = this.numeric(unit.consumed) + this.numeric(unit.wasted);
    return outflow > 0 ? (this.numeric(unit.wasted) / outflow) * 100 : null;
  }

  /** Average monthly use. Null for all-time, where the span isn't knowable. */
  get consumedPerMonth(): DisplayAmount | null {
    const unit = this.primaryUnit;
    const days = this.insightsWindowDays;
    if (!unit || !days) return null;
    const months = days / 30.44;
    if (months < 1) return null;
    return this.displayAmount(this.numeric(unit.consumed) / months, unit.unit);
  }

  get insightsPurchases(): number {
    return this.insights?.coverage?.purchases ?? 0;
  }

  get insightsPricedPurchases(): number {
    return this.insights?.coverage?.priced_purchases ?? 0;
  }

  get insightsCoveragePct(): number | null {
    const purchases = this.insightsPurchases;
    return purchases > 0 ? (this.insightsPricedPurchases / purchases) * 100 : null;
  }

  /** Typical gap between purchases. Null for all-time. */
  get daysBetweenPurchases(): number | null {
    const days = this.insightsWindowDays;
    return days && this.insightsPurchases > 1 ? days / this.insightsPurchases : null;
  }

  /** Average cost of a purchase, over the priced ones only. */
  get spendPerPurchase(): number | null {
    const money = this.primaryMoney;
    const priced = this.insightsPricedPurchases;
    return money && priced > 0 ? this.numeric(money.spent) / priced : null;
  }

  /**
   * One cell per purchase for the coverage meter. Above 60 purchases the cells
   * stand for a proportional share instead, so the meter stays a meter.
   */
  get coverageCells(): boolean[] {
    const purchases = this.insightsPurchases;
    if (purchases <= 0) return [];
    const cells = Math.min(purchases, 60);
    const priced = cells === purchases
      ? this.insightsPricedPurchases
      : Math.round((this.insightsPricedPurchases / purchases) * cells);
    return Array.from({length: cells}, (_, i) => i < priced);
  }

  private formatWindowLabel(from: Date | null, to: Date): string {
    const fmt = (d: Date) => d.toLocaleDateString(undefined, {day: 'numeric', month: 'short', year: 'numeric'});
    return from ? `${fmt(from)} – ${fmt(to)}` : `Everything up to ${fmt(to)}`;
  }

  statusContextClass(status: string) {
    switch ((status || '').toLowerCase()) {
      case 'success': case 'ok': return 'bg-success/10 text-success';
      case 'error': case 'failed': return 'bg-error/10 text-error';
      case 'warning': case 'pending': return 'bg-warning/10 text-warning';
      default: return 'bg-info/10 text-info';
    }
  }

  statusBadgeClass(status: string) {
    switch ((status || '').toLowerCase()) {
      case 'success':
      case 'ok':
        return 'badge-success';
      case 'warning':
      case 'pending':
        return 'badge-warning';
      case 'error':
      case 'failed':
        return 'badge-error';
      default:
        return 'badge-info';
    }
  }

  providerBadgeClass(status: string) {
    // gleiche Logik, aber outline/softer möglich
    return this.statusBadgeClass(status);
  }

  /** Background color class for the compact sync status dot in the detail card. */
  syncDotClass(status: string) {
    switch ((status || '').toLowerCase()) {
      case 'success': case 'ok': return 'bg-success';
      case 'warning': return 'bg-warning';
      case 'pending': return 'bg-info';
      case 'error': case 'failed': return 'bg-error';
      default: return 'bg-info';
    }
  }

  /** Human-friendly label for the aggregated sync status. */
  syncStatusLabel(status: string) {
    switch ((status || '').toLowerCase()) {
      case 'success': case 'ok': return 'Synced';
      case 'warning': return 'Partially synced';
      case 'pending': return 'Syncing…';
      case 'error': case 'failed': return 'Sync failed';
      default: return status || 'Unknown';
    }
  }

  okCount(providers: any[] = []) {
    return providers.filter(p => ['ok', 'success'].includes((p.status || '').toLowerCase())).length;
  }


  activeProvider: string | null = null;

  toggleProvider(provider: string, ev: MouseEvent) {
    this.log.debug("Toggling provider details", {provider, event: ev});
    ev.preventDefault();
    ev.stopPropagation();
    this.activeProvider = this.activeProvider === provider ? null : provider;
  }

  @HostListener('document:click')
  closeOnOutsideClick() {
    this.log.debug("Closing provider details (outside click)")
    this.activeProvider = null;
    this.showAllProviders = false;
    this.pickerOpenIndex = null;
  }

  trackProvider(_: number, p: any) {
    return p.provider;
  }

  closeProvider(ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    this.log.debug("Closing provider details")
    queueMicrotask(() => (this.activeProvider = null));
  }

  showAllProviders = false;

  toggleAllProviders(ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    this.showAllProviders = !this.showAllProviders;
  }

  closeAllProviders(ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    queueMicrotask(() => (this.showAllProviders = false));
  }

  aggregatedBadgeClass(providers: ProviderStatus[]) {
    const statuses = providers.map(p => (p.status || '').toLowerCase());
    if (statuses.some(s => s === 'error' || s === 'failed')) return 'badge-error';
    if (statuses.some(s => s === 'warning' || s === 'pending')) return 'badge-warning';
    if (statuses.some(s => s === 'ok' || s === 'success')) return 'badge-success';
    return 'badge-info';
  }

  aggregatedProviderText(providers: ProviderStatus[]) {
    const ok = providers.filter(p => ['ok', 'success'].includes((p.status || '').toLowerCase())).length;
    return `${ok}/${providers.length} ok`;
  }

  maxLastSynced(providers: ProviderStatus[]): string | null {
    const times = providers.map(p => p.last_synced_at).filter(Boolean).map(d => new Date(d!).getTime());
    return times.length ? new Date(Math.max(...times)).toISOString() : null;
  }

  onProductImageError(): void {
    this.log.error('onProductImageError() called');
    this.productImageLoadError = true;
  }

  onProductImageLoad(): void {
    this.productImageLoadError = false;
  }

  async deleteAttribute(attr: ProductAttribute): Promise<void> {
    const ok = await this.confirm.ask({
      title: 'Delete attribute',
      message: `Delete "${attr.name}" from ${this.getProviderName(attr.provider)}?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmBtnClass: 'btn-danger',
    });
    if (!ok) return;

    this.productService.deleteProductAttribute(this.productId, attr.provider, attr.name).subscribe({
      next: () => {
        this.toastService.success(`Attribute "${attr.name}" deleted.`);
        this.product.attributes = this.product.attributes?.filter(
          a => !(a.provider === attr.provider && a.name === attr.name)
        );
      },
      error: (err) => {
        this.toastService.error(`Failed to delete attribute "${attr.name}".`);
        this.log.error('Failed to delete attribute', err);
      }
    });
  }

  protected readonly faImage = faImage;
  protected readonly faSearch = faMagnifyingGlass;
  protected readonly faBarcode = faBarcode;

  get primaryCode() {
    return this.product?.codes?.find(c => c.is_primary) ?? this.product?.codes?.[0];
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    const newMax = window.innerWidth >= 640 ? 3 : 2;
    if (newMax !== this.maxCompareProviders) {
      this.maxCompareProviders = newMax;
      this.syncSelectedProviders();
    }
  }

  private initCompareProviders(): void {
    const providers = this.attributesByProvider.map(g => g.provider);
    this.selectedProviders = providers.slice(0, this.maxCompareProviders);
  }

  private syncSelectedProviders(): void {
    const all = this.attributesByProvider.map(g => g.provider);
    const max = this.maxCompareProviders;
    if (this.selectedProviders.length > max) {
      this.selectedProviders = this.selectedProviders.slice(0, max);
    } else if (this.selectedProviders.length < max) {
      const extra = all.filter(p => !this.selectedProviders.includes(p)).slice(0, max - this.selectedProviders.length);
      this.selectedProviders = [...this.selectedProviders, ...extra];
    }
  }

  get comparisonRows(): { name: string; values: { [p: string]: ProductAttribute | null }; cellScale: Map<string, number> }[] {
    const allNames = new Set<string>();
    for (const provider of this.selectedProviders) {
      (this.product?.attributes ?? [])
        .filter(a => a.provider === provider)
        .forEach(a => allNames.add(a.name));
    }
    return Array.from(allNames).map(name => {
      const values: { [p: string]: ProductAttribute | null } = {};
      for (const provider of this.selectedProviders) {
        values[provider] = this.product?.attributes?.find(
          a => a.provider === provider && a.name === name
        ) ?? null;
      }
      const dateScale = this.computeDateScale(values);
      const cellScale = dateScale.size > 0 ? dateScale : this.computePriceScale(name, values);
      return { name, values, cellScale };
    });
  }

  private extractPrice(value: string): number | null {
    if (!value) return null;
    const match = value.match(/(\d+[.,]\d+|\d+)/);
    if (!match) return null;
    const num = parseFloat(match[1].replace(',', '.'));
    return isNaN(num) ? null : num;
  }

  private computePriceScale(
    name: string,
    values: { [p: string]: ProductAttribute | null }
  ): Map<string, number> {
    const result = new Map<string, number>();
    if (!/price/i.test(name)) return result;

    const extracted: { provider: string; price: number }[] = [];
    for (const provider of this.selectedProviders) {
      const attr = values[provider];
      if (!attr) continue;
      const price = this.extractPrice(attr.value);
      if (price !== null) extracted.push({ provider, price });
    }

    if (extracted.length < 2) return result;

    const prices = extracted.map(e => e.price);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    if (min === max) return result;

    for (const { provider, price } of extracted) {
      result.set(provider, (max - price) / (max - min)); // 0 = most expensive (red), 1 = cheapest (green)
    }
    return result;
  }

  private isDateAttr(attr: ProductAttribute): boolean {
    if ((attr.type ?? '').toLowerCase() === 'datetime') return true;
    const v = (attr.value ?? '').trim();
    if (!v || !isNaN(Number(v))) return false;
    return !isNaN(new Date(v).getTime());
  }

  private computeDateScale(values: { [p: string]: ProductAttribute | null }): Map<string, number> {
    const result = new Map<string, number>();
    const entries: { provider: string; time: number }[] = [];

    for (const provider of this.selectedProviders) {
      const attr = values[provider];
      if (!attr || !this.isDateAttr(attr)) continue;
      const t = new Date(attr.value).getTime();
      if (!isNaN(t)) entries.push({ provider, time: t });
    }

    if (entries.length < 2) return result;

    const times = entries.map(e => e.time);
    const min = Math.min(...times);
    const max = Math.max(...times);
    if (min === max) return result;

    for (const { provider, time } of entries) {
      result.set(provider, (time - min) / (max - min)); // 0 = oldest (red), 1 = newest (green)
    }
    return result;
  }

  cellScaleStyle(scale: number): { [k: string]: string } {
    const hue = Math.round(scale * 120); // 0 = red, 120 = green
    return { 'background-color': `hsla(${hue}, 80%, 45%, 0.13)` };
  }

  openProviderPicker(index: number, event: MouseEvent): void {
    if (this.availableProvidersForSlot(index).length === 0) return;
    event.stopPropagation();
    this.pickerOpenIndex = this.pickerOpenIndex === index ? null : index;
  }

  swapProvider(slotIndex: number, newProvider: string, event: MouseEvent): void {
    event.stopPropagation();
    this.selectedProviders = this.selectedProviders.map((p, i) => i === slotIndex ? newProvider : p);
    this.pickerOpenIndex = null;
  }

  sortedSyncProviders(providers: ProviderStatus[]): ProviderStatus[] {
    return [...providers].sort((a, b) => this.providerPriority(a.provider) - this.providerPriority(b.provider));
  }

  availableProvidersForSlot(slotIndex: number): string[] {
    const allSelected = new Set(this.selectedProviders);
    return this.attributesByProvider.map(g => g.provider).filter(p => !allSelected.has(p));
  }

  detectAttributeFormat(attr: ProductAttribute): 'url' | 'html' | 'number' | 'boolean' | 'text' {
    const type = (attr.type || '').toLowerCase();
    const value = String(attr.value ?? '').trim();

    if (type === 'url' || /^https?:\/\//i.test(value)) return 'url';
    if (type === 'money' || type === 'datetime') return 'text';
    if (type === 'boolean' || value === 'true' || value === 'false') return 'boolean';
    if (/<[a-z][\s\S]*?>/i.test(value)) return 'html';
    if (value !== '' && !isNaN(Number(value))) return 'number';
    return 'text';
  }

  get attributesByProvider(): { provider: string; attributes: ProductAttribute[] }[] {
    const attrs = this.product?.attributes ?? [];
    const map = new Map<string, ProductAttribute[]>();
    for (const attr of attrs) {
      const key = attr.provider ?? '';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(attr);
    }
    return Array.from(map.entries())
      .map(([provider, attributes]) => ({ provider, attributes }))
      .sort((a, b) => this.providerPriority(a.provider) - this.providerPriority(b.provider));
  }

  openCodesModal(): void {
    if (this.product?.codes?.length) {
      this.showCodesModal = true;
    }
  }

  closeCodesModal(): void {
    this.showCodesModal = false;
  }

  setPrimaryCode(code: ProductCode): void {
    this.productService.setCodeAsPrimary(this.productId, code.code!).subscribe({
      next: () => {
        this.product.codes = this.product.codes!.map(c => ({
          ...c,
          is_primary: c.code === code.code,
        }));
        this.toastService.success(`"${code.code}" set as primary`);
      },
      error: () => {
        this.toastService.error('Failed to set primary code');
      }
    });
  }
}
